import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { AddAPIErrorAlert, AddAlert } from '../../alerts/store/alerts.action';
import { from, Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap } from 'rxjs/operators';
import {
    ApiError,
    ApiResponse
} from '../../../../common/models/api-response.interface';
import {
    clearState,
    GetPartnerDataRequest,
    GetPartnerDataRequestFailure,
    GetPartnerDataRequestSuccess,
    GetPreferredPaymentInfoRequest,
    GetPreferredPaymentInfoRequestSuccess,
    GetPreferredPaymentInfoRequestFailure,
    SetPreferredPaymentInfoRequest,
    SetPreferredPaymentInfoRequestSuccess,
    SetPreferredPaymentInfoRequestFailure
} from './shared.actions';
import { SharedStoreService } from './shared-store.service';
import { PartnerDetail } from '../../../../common/models/search.interface';
import { GetUserPermissionsRequest } from '../../utils/store/rbac/rbac.actions';
import { GetAppMaintenanceModeStatusRequest } from '../../utils/store/common/common.actions';
import { Router } from '@angular/router';
import { AlertSeverity } from '../../../../common/models/alert.interface';

@Injectable()
export class SharedEffects {
    partnerPicklist$ = createEffect(() =>
        this.actions$.pipe(
            filter((action: Action) => action instanceof GetPartnerDataRequest),
            mergeMap((action: GetPartnerDataRequest) => {
                return this.sharedService.getPartnerData().pipe(
                    map((response: ApiResponse<PartnerDetail[]>) => {
                        if (!response.data?.length) {
                            throw {
                                error: new ApiError(
                                    'Invalid/empty partner picklist data',
                                    409
                                )
                            };
                        }
                        return new GetPartnerDataRequestSuccess(response.data);
                    }),
                    catchError((err) =>
                        of(
                            new AddAPIErrorAlert(err.error),
                            new GetPartnerDataRequestFailure(err)
                        )
                    )
                );
            })
        )
    );

    clearState$ = createEffect(() =>
        this.actions$.pipe(
            ofType(clearState),
            switchMap(() =>
                from(this.router.navigateByUrl('/')).pipe(
                    mergeMap(() => [
                        new GetUserPermissionsRequest(),
                        new GetAppMaintenanceModeStatusRequest()
                    ]),
                    catchError((err) => {
                        console.error('Navigation or reset error:', err);
                        return of(new AddAPIErrorAlert(err));
                    })
                )
            )
        )
    );

    getPreferredPaymentMode$ = createEffect(() =>
        this.actions$.pipe(
            filter(
                (action: Action) =>
                    action instanceof GetPreferredPaymentInfoRequest
            ),
            switchMap((action: GetPreferredPaymentInfoRequest) =>
                this.sharedService.getPreferredPaymentMode(action.payload).pipe(
                    map((response) => {
                        return new GetPreferredPaymentInfoRequestSuccess(
                            response.data ?? {}
                        );
                    }),
                    catchError((err) =>
                        of(new GetPreferredPaymentInfoRequestFailure(err))
                    )
                )
            )
        )
    );

    setPreferredPaymentMode$ = createEffect(() =>
        this.actions$.pipe(
            filter(
                (action: Action) =>
                    action instanceof SetPreferredPaymentInfoRequest
            ),
            switchMap((action: SetPreferredPaymentInfoRequest) =>
                this.sharedService.setPreferredPaymentMode(action.payload).pipe(
                    mergeMap((response) => {
                        return of(
                            new AddAlert({
                                message:
                                    response.data?.message ??
                                    'Successfully set preferred payment details!',
                                severity: AlertSeverity.SUCCESS
                            }),
                            new SetPreferredPaymentInfoRequestSuccess(
                                response.data
                            ),
                            new GetPreferredPaymentInfoRequest(
                                action.payload.searchToken
                            )
                        );
                    }),
                    catchError((err) =>
                        of(
                            new AddAPIErrorAlert(err.error),
                            new SetPreferredPaymentInfoRequestFailure(err)
                        )
                    )
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private sharedService: SharedStoreService,
        private router: Router
    ) {}
}
